import React from "react";
import type { APAADMINBaseModelsNetworkStatusTargetView } from "../../API/Models";
import { PanelCard, severityStyle } from "./dashboardShared";
import { targetSeverity } from "./NetworkTelemetry";

/** Find a monitored target by its backend key (the generated enum omits some, so match by string). */
const find = (targets: APAADMINBaseModelsNetworkStatusTargetView[], key: string) => targets.find((t) => (t.target as string) === key);

const latencyOf = (t?: APAADMINBaseModelsNetworkStatusTargetView): string | null => {
	const latest = t?.latest;
	if (!latest || !latest.sampledAt) return null;
	if (!latest.isSuccess) return null;
	return `${Math.round(latest.pingMs ?? 0)} ms`;
};

const stateText = (severity: number): string =>
	severity === 2 ? "En ligne" : severity === 0 ? "Hors service" : severity === 1 ? "Ralenti" : "Inconnu";

const StatusRow: React.FC<{ label: string; severity: number; detail?: string | null }> = ({ label, severity, detail }) => {
	const style = severityStyle(severity);
	return (
		<li className="flex items-center gap-3 px-5 py-3">
			<span className={`h-2.5 w-2.5 shrink-0 rounded-full ${style.dot}`} aria-hidden />
			<span className="text-sm font-semibold text-slate-700">{label}</span>
			<span className={`ml-auto text-xs font-medium ${style.text}`}>{detail ?? stateText(severity)}</span>
		</li>
	);
};

export interface ServiceStatusProps {
	targets: APAADMINBaseModelsNetworkStatusTargetView[];
	/** Admin API has no dedicated probe — realtime connectivity is the live proxy for "is it reachable". */
	connected: boolean;
}

/** Health of the four things an admin needs up: the two public sites, the admin API, and the database. */
export const ServiceStatus: React.FC<ServiceStatusProps> = ({ targets, connected }) => {
	const preview = find(targets, "PublicPreviewSite");
	const prod = find(targets, "PublicSite");
	const db = find(targets, "SqlServer");

	const previewSeverity = targetSeverity(preview?.latest);
	const prodSeverity = targetSeverity(prod?.latest);
	const dbSeverity = targetSeverity(db?.latest);
	const adminSeverity = connected ? 2 : 0;

	return (
		<PanelCard title="Statut des services" subtitle="Santé de l'infrastructure" bodyClassName="py-1">
			<ul className="divide-y divide-slate-100">
				<StatusRow label="Préversion" severity={previewSeverity} detail={latencyOf(preview)} />
				<StatusRow label="Production" severity={prodSeverity} detail={latencyOf(prod)} />
				<StatusRow label="Admin (API)" severity={adminSeverity} />
				<StatusRow label="Base de données" severity={dbSeverity} detail={latencyOf(db)} />
			</ul>
		</PanelCard>
	);
};

export default ServiceStatus;
